/**
 * Floating avatar + handle chip rendered above a player-authored tower so the
 * portal reads as "made by me, placed here". Bobs vertically; tracked by
 * RunnerScene's group-translation pass so it scrolls with the tower.
 *
 * The overlay is rendered into a Phaser.GameObjects.Container that the caller
 * positions absolutely each frame (no parent-relative anchoring — RunnerScene
 * already does its own scroll bookkeeping for ground entities). All children
 * live inside the container at offsets relative to the tower top.
 */

import Phaser from "phaser";

import { GAMEPLAY, PALETTE, UGC_CYAN } from "../config";
import type { AuthorIdentity } from "../types";

import type { AuthorIdentityService } from "../services/AuthorIdentityService";

const CHIP_HEIGHT = 18;
const AVATAR_SIZE = 14;
const NAME_PADDING = 3;
const CHIP_BORDER = 1;

export class AuthorTowerOverlay {
  readonly container: Phaser.GameObjects.Container;
  private readonly avatar: Phaser.GameObjects.Image;
  private readonly nameLabel: Phaser.GameObjects.Text;
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly bobBaseY: number;
  private readonly bobTween?: Phaser.Tweens.Tween;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    identityService: AuthorIdentityService,
    identity: AuthorIdentity,
  ) {
    this.container = scene.add.container(x, y).setDepth(20);
    this.bobBaseY = y;

    const truncatedName = `@${identity.username.slice(0, 10)}`;
    // Measure the label so the chip background sizes to fit.
    const tmpText = scene.add.text(0, 0, truncatedName, {
      fontFamily: "monospace",
      fontSize: "8px",
    });
    const textWidth = Math.ceil(tmpText.width);
    tmpText.destroy();

    const chipWidth = AVATAR_SIZE + NAME_PADDING + textWidth + NAME_PADDING * 2;

    this.bg = scene.add
      .rectangle(0, 0, chipWidth, CHIP_HEIGHT, PALETTE.authorBadgeBg, 0.95)
      .setOrigin(0.5, 1)
      .setStrokeStyle(CHIP_BORDER, PALETTE.authorBadgeText);

    // Cyan UGC corner-mark, top-right of the chip.
    const cornerX = chipWidth / 2 - 3;
    const cornerY = -CHIP_HEIGHT + 1;
    const corner = scene.add.rectangle(cornerX, cornerY, 2, 2, UGC_CYAN, 1).setOrigin(0, 0);

    const avatarX = -chipWidth / 2 + NAME_PADDING;
    const avatarY = -CHIP_HEIGHT + (CHIP_HEIGHT - AVATAR_SIZE) / 2;
    this.avatar = identityService.drawCircleAvatar(scene, avatarX, avatarY, identity, AVATAR_SIZE);

    this.nameLabel = scene.add
      .text(avatarX + AVATAR_SIZE + NAME_PADDING, -CHIP_HEIGHT / 2, truncatedName, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: `#${PALETTE.authorBadgeText.toString(16).padStart(6, "0")}`,
      })
      .setOrigin(0, 0.5);

    this.container.add([this.bg, corner, this.avatar, this.nameLabel]);

    this.bobTween = scene.tweens.add({
      targets: this.container,
      y: { from: y - GAMEPLAY.authorTowerOverlayBobPx, to: y + GAMEPLAY.authorTowerOverlayBobPx },
      duration: GAMEPLAY.authorTowerOverlayBobMs / 2,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  /** Shift x by `dx` (called from RunnerScene's per-frame scroll translate). */
  translateX(dx: number): void {
    this.container.x += dx;
  }

  /** Update the base y when the tower is anchored at a new height (rare). */
  setBaseY(y: number): void {
    if (Math.abs(this.bobBaseY - y) < 0.01) return;
    // Restart the tween centred on the new y so the bob stays balanced.
    this.bobTween?.remove();
    this.container.y = y;
    // intentionally no new tween — sub-level transitions destroy + recreate.
  }

  destroy(): void {
    this.bobTween?.remove();
    this.container.destroy();
  }
}
